import {
  amplitude,
  AmplitudeLogLevel,
  AppAmplitudeLogger,
} from '@sennder/shell-utilities'

import { AMPLITUDE_API_KEY } from '@/common/config'
import { getStateData, getStateCompany, getStateProfile } from '@/store/getters'
import { executeSafelyWithLogging } from './errors-handler'
import { logger } from './logger/loggers'
import { parseTenantFromToken } from './tokenManager'

export function initAnalytics() {
  return executeSafelyWithLogging(() => {
    if (!AMPLITUDE_API_KEY) {
      throw new Error('[orcas-shell - Analytics]: AMPLITUDE_API_KEY not found')
    }

    amplitude.init({
      token: AMPLITUDE_API_KEY,
      appName: 'carrier-portal',
      amplitudeConfig: {
        minIdLength: 4,
        loggerProvider: new AppAmplitudeLogger(logger),
        logLevel: AmplitudeLogLevel.Warn,
        autocapture: {
          attribution: {
            excludeReferrers: [],
            initialEmptyValue: '',
          },
          pageViews: false,
          sessions: false,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
        },
      },
    })
  }, 'amplitude.init')
}

export async function identifyUserInAnalytics(onLogin: boolean) {
  const profile = getStateProfile()
  const cpsCarrier = getStateData().cpsCarrier
  const isInternalUser =
    profile.email.endsWith('@sennder.com') ||
    profile.email.endsWith('@sennder.it')

  const analyticsUser = {
    tenant: await parseTenantFromToken(),
    userId: profile.id,
    sennderSubsidiaryId: cpsCarrier?.billing?.sennder_subsidiary_id,
    language: profile.language,
    hasCarrierPortalAccess: getStateData().hasOrcasAccess,
    isInternalUser,
  }

  const deprecatedUserProps: string[] = [
    'initial_dclid',
    'initial_fbclid',
    'initial_gbraid',
    'initial_gclid',
    'initial_ko_click_id',
    'initial_li_fat_id',
    'initial_msclkid',
    'initial_referrer',
    'initial_referring_domain',
    'initial_rtd_cid',
    'initial_ttclid',
    'initial_twclid',
    'initial_utm_campaign',
    'initial_utm_content',
    'initial_utm_id',
    'initial_utm_medium',
    'initial_utm_source',
    'initial_utm_term',
    'initial_wbraid',
    'referrer',
    'referring_domain',
    'Carriers',
    'carrierType',
    'carrier id',
    'carrierId',
    'carrierUuid',
    'exp_ab-first-aa-test',
    'id',
    'hasOrcasAccess',
    'orgType',
    'anonymousId',
  ]

  executeSafelyWithLogging(
    () =>
      amplitude.identifyUser(
        analyticsUser,
        onLogin,
        {
          groupType: 'carrierUuid',
          groupValue: `${getStateCompany().cpsCarrierId}`,
        },
        deprecatedUserProps
      ),
    'amplitude.identifyUser'
  )
}

export const resetAnalyticsSession = () => {
  stopSessionReplay()
  executeSafelyWithLogging(() => amplitude.endSession(), 'amplitude.endSession')
  initAnalytics()
}

// Used when the user rejects analytics cookies,
// all user data will be removed from the analytics integration as well as any event submissions will be terminated
export const stopAnalytics = () => {
  executeSafelyWithLogging(
    () => amplitude.stopTracking(),
    'amplitude.stopTracking'
  )
}

export function initSessionReplay() {
  executeSafelyWithLogging(
    () =>
      amplitude.initSessionReplay({
        sampleRate: 0.8,
      }),
    'amplitude.initSessionReplay'
  )
}

export function stopSessionReplay() {
  executeSafelyWithLogging(
    () => amplitude.stopSessionReplay(),
    'amplitude.stopSessionReplay'
  )
}
